/**
 * i18n configuration — shared between server and client.
 *
 * Translations are bundled directly (no HTTP backend). This is required because:
 *  - The server runs in Cloudflare Workers (no filesystem, no self-fetch).
 *  - Bundling avoids a waterfall on first render.
 *
 * The root loader passes `i18nStore` as serialised loader data so the client
 * hydrates with the same translation bundle and `useTranslation` works
 * immediately on mount — no flicker, no second network request.
 *
 * Usage in components:
 *   const { t } = useTranslation();
 *   t("login.heading") // → "POS Developer Portal"
 *
 * Usage outside React (e.g. meta(), loaders):
 *   import { i18n } from "~/lib/i18n";
 *   i18n.t("login.meta.title")
 *
 * Source of truth for translations: app/locales/source/
 * Each key carries { translation, limit, note? }. This module extracts the
 * translation strings into a flat map keyed by the dot-notation key.
 * i18next is configured with keySeparator: false so t("login.heading") looks
 * up the literal key — no nested-object traversal needed.
 */
import i18next from "i18next";
import { initReactI18next } from "react-i18next";

import enSource from "../locales/source/en.json";
import arSource from "../locales/source/ar.json";
import trSource from "../locales/source/tr.json";
import esSource from "../locales/source/es.json";
import esESSource from "../locales/source/es-ES.json";

export const defaultNS = "translation";
export const defaultLng = "en";

export const SUPPORTED_LANGUAGES = ["en", "ar", "tr", "es", "es-ES"] as const;
export type SupportedLanguage = (typeof SUPPORTED_LANGUAGES)[number];

type SourceFile = Record<string, { translation: string }>;

// Extracts translation strings from the annotated source format and falls back
// to English for any empty entry. Returns a flat map — no nested traversal.
function buildTranslations(source: SourceFile, fallback: SourceFile): Record<string, string> {
  const result: Record<string, string> = {};
  for (const key of Object.keys(fallback)) {
    const entry = fallback[key];
    if (!entry) continue;
    result[key] = source[key]?.translation || entry.translation;
  }
  return result;
}

export const resources: Record<SupportedLanguage, { translation: Record<string, string> }> = {
  en:      { translation: buildTranslations(enSource, enSource) },
  ar:      { translation: buildTranslations(arSource, enSource) },
  tr:      { translation: buildTranslations(trSource, enSource) },
  es:      { translation: buildTranslations(esSource, enSource) },
  "es-ES": { translation: buildTranslations(esESSource, enSource) },
};

// i18next is a singleton. Guard against re-initialisation during HMR — but
// when this module re-evaluates because a locale JSON changed, replace each
// resource bundle so dev pickup is immediate (no server restart required).
if (!i18next.isInitialized) {
  void i18next.use(initReactI18next).init({
    lng: defaultLng,
    fallbackLng: defaultLng,
    defaultNS,
    resources,
    // Resources are flat dot-notation keys; disable splitting so lookup
    // uses the full key string rather than traversing a nested object.
    keySeparator: false,
    interpolation: {
      // React already handles XSS escaping.
      escapeValue: false,
    },
  });
} else {
  for (const lng of SUPPORTED_LANGUAGES) {
    i18next.addResourceBundle(lng, defaultNS, resources[lng].translation, true, true);
  }
}

export { i18next as i18n };
